import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import FloatingChatWidget from '../components/FloatingChatWidget';

const HelpPage = () => {
  const [activeSection, setActiveSection] = useState(-1);

  const helpSections = [
    {
      icon: 'fas fa-user-plus',
      title: 'Getting Started with Tazza Tropics',
      description: 'Learn how to create your account and start ordering fresh meat',
      content: 'Join Tazza Tropics to get premium quality meat delivered fresh to your doorstep. Create your account and discover our wide range of fresh cuts.'
    },
    {
      icon: 'fas fa-shopping-cart',
      title: 'Placing Orders',
      description: 'Step-by-step guide to ordering fresh meat from Tazza Tropics',
      content: 'Browse our premium meat selection including chicken, mutton, beef, and seafood. Choose from various cuts, select quantities, and enjoy doorstep delivery.'
    },
    {
      icon: 'fas fa-truck',
      title: 'Delivery & Freshness',
      description: 'Everything about our delivery service and freshness guarantee',
      content: 'Experience our commitment to freshness with temperature-controlled delivery. Track your order in real-time and choose convenient delivery slots.'
    },
    {
      icon: 'fas fa-credit-card',
      title: 'Payment & Security',
      description: 'Learn about secure payment options at Tazza Tropics',
      content: 'Multiple secure payment methods including online payments, UPI, wallets, and cash on delivery. Your transactions are always protected.'
    },
    {
      icon: 'fas fa-user-cog',
      title: 'Account & Preferences',
      description: 'Manage your Tazza Tropics account and delivery preferences',
      content: 'Update your profile, manage multiple delivery addresses, track order history, and customize your meat preferences for better recommendations.'
    },
    {
      icon: 'fas fa-tools',
      title: 'Support & Troubleshooting',
      description: 'Get help with common issues and contact our support team',
      content: 'Find solutions to login issues, order problems, delivery concerns, and get instant support from our dedicated customer care team.'
    }
  ];

  const toggleSection = (index) => {
    setActiveSection(activeSection === index ? -1 : index);
  };

  return (
    <>
      <Header />
      <main className="main__content_wrapper">
        <div className="container py-5">
          <div className="help-header text-center mb-5">
            <h1 className="help-title">Tazza Tropics Help Center</h1>
            <p className="help-subtitle">Get instant help with your fresh meat delivery orders and account</p>
          </div>

          <div className="help-search mb-5">
            <div className="search-box mx-auto" style={{maxWidth: '500px'}}>
              <i className="fas fa-search search-icon"></i>
              <input type="text" placeholder="Search help topics..." className="search-input form-control" />
            </div>
          </div>

          <div className="help-grid row">
            {helpSections.map((section, index) => (
              <div key={index} className="col-md-6 col-lg-4 mb-4">
                <div 
                  className="help-card card h-100"
                  onClick={() => toggleSection(index)}
                  style={{cursor: 'pointer'}}
                >
                  <div className="card-body text-center">
                    <div className="card-icon mb-3">
                      <i className={section.icon} style={{fontSize: '2rem', color: '#b91c1c'}}></i>
                    </div>
                    <h5 className="card-title">{section.title}</h5>
                    <p className="card-description">{section.description}</p>
                    <div className="expand-icon">
                      <i className={`fas ${activeSection === index ? 'fa-minus' : 'fa-plus'}`}></i>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {activeSection !== -1 && (
            <div className="help-details mt-5">
              <div className="detail-card card">
                <div className="card-body">
                  <div className="detail-header d-flex justify-content-between align-items-center mb-4">
                    <div className="d-flex align-items-center">
                      <div className="detail-icon me-3">
                        <i className={helpSections[activeSection].icon} style={{fontSize: '1.5rem', color: '#b91c1c'}}></i>
                      </div>
                      <h4 className="detail-title mb-0">{helpSections[activeSection].title}</h4>
                    </div>
                    <button className="btn btn-outline-secondary" onClick={() => toggleSection(-1)}>
                      <i className="fas fa-times"></i>
                    </button>
                  </div>
                  <div className="detail-content">
                    {activeSection === 0 && (
                      <div>
                        <h5>Welcome to Tazza Tropics!</h5>
                        <p>Your trusted partner for premium quality fresh meat delivery. Getting started is simple:</p>
                        <ol>
                          <li><strong>Create Your Account:</strong> Sign up with your email or phone number for exclusive access to fresh meat deals</li>
                          <li><strong>Verify Your Account:</strong> Complete verification through email or SMS for secure ordering</li>
                          <li><strong>Add Delivery Address:</strong> Set up multiple delivery addresses for convenient ordering</li>
                          <li><strong>Start Shopping:</strong> Browse our curated selection of chicken, mutton, beef, and seafood cuts</li>
                          <li><strong>First Order:</strong> Place your first order and experience our commitment to freshness</li>
                        </ol>
                        <p><strong>Why Choose Tazza Tropics?</strong> Premium quality, hygienic processing, fast delivery, and 100% freshness guarantee.</p>
                      </div>
                    )}
                    {activeSection === 1 && (
                      <div>
                        <h5>How to Order from Tazza Tropics</h5>
                        <p>Ordering fresh, quality meat has never been easier with our user-friendly platform:</p>
                        <ol>
                          <li><strong>Browse Categories:</strong> Explore our wide range of fresh meat including Chicken, Mutton, Beef, and Seafood</li>
                          <li><strong>Select Premium Cuts:</strong> Choose from various cuts like breast, thigh, curry pieces, and specialty items</li>
                          <li><strong>Choose Quantities:</strong> Select from 250g to 2kg+ portions to suit your needs</li>
                          <li><strong>Add to Cart:</strong> Build your order with multiple items for convenient shopping</li>
                          <li><strong>Apply Coupons:</strong> Use promotional codes for exclusive discounts on fresh meat</li>
                          <li><strong>Secure Checkout:</strong> Complete your order with multiple payment options</li>
                          <li><strong>Track Delivery:</strong> Get real-time updates on your order status</li>
                        </ol>
                        <p><strong>Pro Tip:</strong> Subscribe to our weekly plans for consistent fresh meat delivery at discounted rates!</p>
                      </div>
                    )}
                    {activeSection === 2 && (
                      <div>
                        <h5>Tazza Tropics Delivery Promise</h5>
                        <p>We are committed to delivering the freshest meat with our advanced cold-chain logistics:</p>
                        <ul>
                          <li><strong>Temperature-Controlled Delivery:</strong> All orders delivered in specially designed insulated boxes</li>
                          <li><strong>Delivery Hours:</strong> Available 7 AM to 10 PM daily for your convenience</li>
                          <li><strong>Real-Time Tracking:</strong> Track your order from our facility to your doorstep</li>
                          <li><strong>Freshness Guarantee:</strong> 100% satisfaction guarantee or full refund</li>
                          <li><strong>Contactless Delivery:</strong> Safe, hygienic delivery with no-touch options</li>
                          <li><strong>Express Delivery:</strong> Same-day delivery available in select areas</li>
                          <li><strong>Scheduled Delivery:</strong> Choose your preferred delivery time slot</li>
                        </ul>
                        <p><strong>Quality Assurance:</strong> Every order undergoes strict quality checks before dispatch.</p>
                      </div>
                    )}
                    {activeSection === 3 && (
                      <div>
                        <h5>Secure Payment at Tazza Tropics</h5>
                        <p>Your security is our priority. We offer multiple safe and convenient payment options:</p>
                        <ul>
                          <li><strong>Credit/Debit Cards:</strong> Visa, Mastercard, American Express, RuPay</li>
                          <li><strong>UPI Payments:</strong> Google Pay, PhonePe, Paytm, BHIM UPI</li>
                          <li><strong>Digital Wallets:</strong> Paytm, Mobikwik, Ola Money, and more</li>
                          <li><strong>Net Banking:</strong> Direct bank transfers from all major banks</li>
                          <li><strong>Cash on Delivery:</strong> Pay in cash upon delivery (additional charges may apply)</li>
                          <li><strong>Wallet Points:</strong> Use Tazza Tropics reward points for discounts</li>
                        </ul>
                        <p><strong>Security Features:</strong> 256-bit SSL encryption, PCI DSS compliance, and secure payment gateways.</p>
                        <p><strong>Payment Issues?</strong> Contact our support team immediately for resolution.</p>
                      </div>
                    )}
                    {activeSection === 4 && (
                      <div>
                        <h5>Managing Your Tazza Tropics Account</h5>
                        <p>Take full control of your account and personalize your meat shopping experience:</p>
                        <ul>
                          <li><strong>Profile Management:</strong> Update personal details, contact information, and preferences</li>
                          <li><strong>Multiple Addresses:</strong> Save home, office, and other delivery addresses</li>
                          <li><strong>Order History:</strong> View all past orders, reorder favorites, and track spending</li>
                          <li><strong>Meat Preferences:</strong> Set preferences for cuts, quantities, and dietary requirements</li>
                          <li><strong>Subscription Management:</strong> Manage weekly/monthly meat delivery subscriptions</li>
                          <li><strong>Wallet & Rewards:</strong> Track reward points, referral bonuses, and wallet balance</li>
                          <li><strong>Notifications:</strong> Customize alerts for orders, offers, and delivery updates</li>
                        </ul>
                        <p><strong>Account Benefits:</strong> Earn reward points on every order, exclusive member discounts, and priority customer support.</p>
                      </div>
                    )}
                    {activeSection === 5 && (
                      <div>
                        <h5>Tazza Tropics Support & Solutions</h5>
                        <div className="troubleshooting">
                          <div className="mb-4">
                            <h6>🔐 Login & Account Issues</h6>
                            <ul>
                              <li><strong>Forgot Password:</strong> Use "Forgot Password" link or contact support</li>
                              <li><strong>Account Locked:</strong> Wait 30 minutes or contact customer care</li>
                              <li><strong>Verification Issues:</strong> Check spam folder or request new verification</li>
                            </ul>
                          </div>
                          <div className="mb-4">
                            <h6>📦 Order & Delivery Problems</h6>
                            <ul>
                              <li><strong>Order Not Received:</strong> Check order status or contact delivery team</li>
                              <li><strong>Wrong Items:</strong> Report within 1 hour for immediate replacement</li>
                              <li><strong>Quality Issues:</strong> Photo evidence required for quality complaints</li>
                              <li><strong>Delivery Delay:</strong> Track order status or call delivery hotline</li>
                            </ul>
                          </div>
                          <div className="mb-4">
                            <h6>💳 Payment & Refund Issues</h6>
                            <ul>
                              <li><strong>Payment Failed:</strong> Check payment method or try alternative options</li>
                              <li><strong>Double Charged:</strong> Contact support with transaction details</li>
                              <li><strong>Refund Status:</strong> Refunds processed within 5-7 business days</li>
                            </ul>
                          </div>
                          <div className="mb-4">
                            <h6>📱 App & Website Issues</h6>
                            <ul>
                              <li><strong>App Crashes:</strong> Update to latest version or clear app cache</li>
                              <li><strong>Website Loading:</strong> Clear browser cache or try different browser</li>
                              <li><strong>Order Tracking:</strong> Refresh page or check order history</li>
                            </ul>
                          </div>
                        </div>
                        <p><strong>Still Need Help?</strong> Our support team is available 24/7 to assist you.</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="help-contact mt-5">
            <div className="contact-card card">
              <div className="card-body text-center">
                <h3>Still Need Help?</h3>
                <p>Our Tazza Tropics support team is here to assist you 24/7</p>
                <div className="contact-options d-flex justify-content-center gap-3 mt-3">
                  <a href="/chat" className="btn btn-outline-primary">
                    <i className="fas fa-comments me-2"></i>
                    Live Chat
                  </a>
                  <a href="tel:+12484979083" className="btn btn-outline-primary">
                    <i className="fas fa-phone me-2"></i>
                    Call Us
                  </a>
                  <a href="mailto:contact@tazatropics.com" className="btn btn-outline-primary">
                    <i className="fas fa-envelope me-2"></i>
                    Email Support
                  </a>
                </div>
                <div className="mt-3">
                  <p className="mb-1"><strong>Customer Care:</strong> +1 248 497 9083</p>
                  <p className="mb-1"><strong>Email:</strong> contact@tazatropics.com</p>
                  <p className="mb-0"><strong>Support Email:</strong> help@tazatropics.com</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
      <FloatingChatWidget />
    </>
  );
};

export default HelpPage;